// NotificationSocketService.js
import { io } from "socket.io-client"
import {
	addNotification,
	markAllAsRead,
	markAsRead,
	resetInitialLoad,
	setReadNotificationIds,
} from "../../Reducers/default-reducers/notificationReducer"
import { useWebProvider } from "../../hooks/defaults/useWebProvider"
import { endPointNotification } from "../../models/constantes"
import { store } from "../../store"

/**
 * Cria o socket de notificação. O token deve ser passado pelo caller (ex.: do Redux após login).
 * @param {string} endPointNotification - URL da API de notificação (ex.: http://localhost:4040)
 * @param {string} token - JWT do usuário (obrigatório)
 * @param {string} user - userName
 * @param {string} company - company
 * @param {function} onMessage - callback opcional para mensagens
 */
export function createNotificationSocket(
	endPointNotification,
	token,
	user,
	company,
	onMessage
) {
	const tokenString =
		typeof token === "string" && token ? token.trim() : ""
	if (!tokenString) {
		console.warn("[Notification] Socket não conectado: token ausente. Passe o token ao chamar createNotificationSocket.")
		return null
	}

	const notificationSocket = io(endPointNotification, {
		path: "/notification",
		auth: {
			token: tokenString,
			user,
			company,
			type: "notification",
		},
		extraHeaders: {
			user,
			company,
			token: tokenString,
			type: "notification",
		},
	})
	// Eventos comuns (conexão, erro, etc)
	notificationSocket.on("connect", () => {
		console.log(
			"[Notification] Socket de notificação conectado:",
			notificationSocket.id
		)
		store.dispatch(resetInitialLoad())
	})
	notificationSocket.on("connect_error", (err) => {
		console.log(
			"[Notification] Erro de conexão no socket de notificação:",
			err.message
		)
	})
	notificationSocket.on("disconnect", (reason) => {
		console.log(
			"[Notification] Socket de notificação desconectado:",
			reason
		)
	})
	// Notificações do fluxo legado (outros backends)
	notificationSocket.on("new_notification", (notification) => {
		store.dispatch(addNotification(normalizeNotificationPayload(notification)))
	})
	notificationSocket.on("read_notification", (notification) => {
		const id = notification?.id ?? notification?._id
		if (id != null) store.dispatch(markAsRead(id))
	})
	notificationSocket.on("read_all_notification", (notificationListId) => {
		const ids = Array.isArray(notificationListId) ? notificationListId : [notificationListId]
		if (ids.length) store.dispatch(markAllAsRead(ids))
	})

	// Notificações central (Postgres → notification-api → socket): uma por evento
	// O segundo argumento (ackCallback) é o Socket.IO acknowledgement — o backend
	// só marca como "sent" quando recebe esse ACK de volta.
	notificationSocket.on("central_notification", (payload, ackCallback) => {
		console.log("[Notification] central_notification recebido: id=%s deliveryId=%s", payload?.id, payload?.deliveryId)
		const item = mapCentralPayloadToNotification(payload)
		if (item) store.dispatch(addNotification(item))
		if (typeof ackCallback === "function") {
			ackCallback({ received: true, deliveryId: payload?.deliveryId })
			console.log("[Notification] ACK enviado para deliveryId=%s", payload?.deliveryId)
		}
	})

	// Lote: várias notificações (recuperação na subida da API ou ao conectar / refresh)
	notificationSocket.on("central_notification_batch", (data) => {
		const list = data?.notifications
		if (!Array.isArray(list)) return
		for (const payload of list) {
			const id = payload.id ?? payload.notificationId
			if (payload.read_at != null && id != null) {
				store.dispatch(markAsRead(id))
				continue
			}
			const item = mapCentralPayloadToNotification(payload)
			if (item) store.dispatch(addNotification(item))
		}
	})


	// Ao conectar, o servidor envia notification_user_sync com o estado de leitura do usuário
	notificationSocket.on("notification_user_sync", (data) => {
		const items = data?.items
		if (!Array.isArray(items)) return
		const readIds = items.filter((row) => row.read_at != null && row.delivery_id != null).map((row) => row.delivery_id)
		if (readIds.length) {
			store.dispatch(setReadNotificationIds(readIds))
			readIds.forEach((id) => store.dispatch(markAsRead(id)))
		}
	})

	return notificationSocket
}

/**
 * Formato esperado pelo Redux e NotificationItem: _id, content, status, createdAt, origin, readAt.
 * Garante _id para compatibilidade com markAsRead/markAllAsRead.
 */
function normalizeNotificationPayload(notification) {
	if (!notification) return notification
	const id = notification.id ?? notification._id
	const origin = notification.origin ?? { context: "Notificação", contextId: String(id ?? "") }
	const context = origin.context ?? "Notificação"
	const contextId = origin.contextId ?? String(id ?? "")
	return {
		...notification,
		_id: id,
		origin: {
			...origin,
			title: origin.title ?? `${context} - ${contextId}`,
		},
		status: notification.status ?? "unread",
	}
}

/**
 * Mapeia o payload do evento central_notification (Postgres) para o formato do sino.
 * Inclui title quando existir (ex.: "operação - nome do parâmetro - id"); senão context - contextId.
 */
function mapCentralPayloadToNotification(payload) {
	if (!payload) return null
	const id = payload.id ?? payload.notificationId
	const deliveryId = payload.deliveryId ?? undefined
	const context = payload.context ?? payload.tipo ?? "Notificação"
	const contextId = String(payload.context_id ?? payload.contextId ?? id ?? "")
	return {
		id,
		_id: id,
		deliveryId,
		content: payload.mensagem ?? "",
		status: "unread",
		createdAt: payload.created_at ?? new Date().toISOString(),
		origin: {
			context,
			contextId,
			title: payload.title ?? `${context} - ${contextId}`,
		},
		readAt: null,
		tenant: payload.tenant,
		tipo: payload.tipo,
	}
}
export const useNotificationService = () => {
	const { controller } = useWebProvider()
	const notificationController = controller(null, endPointNotification)

	async function createNotification(form) {
		return notificationController.post("", form)
	}
	async function getNotificationById(id) {
		return notificationController.read("", id)
	}
	/** Um único GET: retorna { notifications, count }. Evita segundo request em /unread/count. */
	async function getUnreadNotificationsForUser(userId) {
		try {
			const result = await notificationController.get(`user/${userId}/unread`)
			if (result && typeof result === "object" && "notifications" in result) {
				return {
					notifications: Array.isArray(result.notifications) ? result.notifications : [],
					count: typeof result.count === "number" ? result.count : 0,
				}
			}
			// Fallback: API antiga retornava só o array
			const list = Array.isArray(result) ? result : []
			return { notifications: list, count: list.length }
		} catch (error) {
			console.error("Error fetching unread notifications:", error)
			return { notifications: [], count: 0 }
		}
	}
	async function getArchivedNotificationsForUser(userId) {
		try {
			const result = await notificationController.get(`user/${userId}/archived`)
			// Ensure we always return an array or object with arrays
			if (!result) return { read: [], dismissed: [] }
			return result
		} catch (error) {
			console.error('Error fetching archived notifications:', error)
			return { read: [], dismissed: [] } // Return empty arrays on error
		}
	}
	async function getNotificationsForUser(userId) {
		return notificationController.get(`user/${userId}`)
	}
	async function markAsRead(id) {
		return notificationController.patch(`${id}/read`)
	}
	async function dismissNotification(id) {
		return notificationController.patch(`${id}/dismiss`)
	}
	async function markAllAsRead(notificationIds) {
		return notificationController.patch(`/mark-all-read`, notificationIds)
	}
	async function deleteNotification(id) {
		return notificationController.delete("", id)
	}
	async function countUnreadNotifications(userId) {
		try {
			const result = await notificationController.get(`user/${userId}/unread/count`)
			// Ensure we always return a number
			return typeof result === 'number' ? result : 0
		} catch (error) {
			console.error('Error counting unread notifications:', error)
			return 0 // Return 0 on error
		}
	}
	return {
		createNotification,
		getNotificationById,
		getNotificationsForUser,
		markAsRead,
		dismissNotification,
		markAllAsRead,
		deleteNotification,
		countUnreadNotifications,
		getUnreadNotificationsForUser,
		getArchivedNotificationsForUser,
	}
}
